/**
 * component-product-form.js -- Variant selection + Add to cart for Mirai Online (Phase 3)
 *
 * Defines TWO Web Components (kept in one file per RESEARCH anti-pattern guidance):
 *
 *  <variant-selector> -- reads the embedded product JSON (no fetch on click) to update
 *    price, compare-at, hidden variant id, the ?variant= URL, and cross-variant
 *    sold-out availability (Pitfall 1: always re-derive from variants, never trust load state).
 *
 *  <add-to-cart-form> -- validates explicit size/colour selection (Pitfall 3), POSTs to
 *    /cart/add.js, and opens the existing Phase 1 cart drawer via
 *    window.CartDrawerHelpers.openCartDrawerWithContent. Button disabled during the
 *    in-flight request (DoS guard T-03-08).
 *
 *  Light DOM only (no Shadow DOM). Both guarded with customElements.get(...).
 *
 * Security notes:
 *  - Variant id is sent to Shopify's /cart/add.js; the server validates inventory and
 *    returns 422 for sold-out, so no client-side availability is authoritative (T-03-05).
 *  - Uses window.Theme.routes.cart_add_url (Shopify CSRF-protected theme endpoint, T-03-06).
 */

/* ============================================================
   VARIANT SELECTOR
   ============================================================ */

class VariantSelector extends HTMLElement {
  constructor() {
    super();
    this._boundClick = this._handleClick.bind(this);
  }

  connectedCallback() {
    this._init();
    this.addEventListener('shopify:section:load', () => this._reinit());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._boundClick);
  }

  _init() {
    const sectionId = this.dataset.sectionId;
    const dataEl = document.getElementById('product-data-' + sectionId);
    try {
      this._product = dataEl ? JSON.parse(dataEl.textContent) : null;
    } catch (e) {
      this._product = null;
    }
    if (!this._product) return;

    // Scope to the PDP section or, when reused inside the Quick View modal, to the
    // .quick-view container — never `document`, so a quick view opened on a PDP page
    // reads its OWN price/variant-id/quantity, not the underlying product's.
    this._section = this.closest('.main-product, .quick-view') || document;
    this._optionGroups = Array.from(this.querySelectorAll('[data-option-index]')).filter(
      (el) => el.tagName === 'FIELDSET'
    );

    // Build initial selection from the pressed buttons (Liquid SSR state)
    this._selectedOptions = [];
    this._optionGroups.forEach((group) => {
      const oi = parseInt(group.dataset.optionIndex, 10);
      const pressed = group.querySelector('[aria-pressed="true"]');
      this._selectedOptions[oi] = pressed ? pressed.dataset.value : null;
    });

    this.addEventListener('click', this._boundClick);
  }

  _reinit() {
    this.removeEventListener('click', this._boundClick);
    this._init();
  }

  _handleClick(e) {
    const btn = e.target.closest('[data-value]');
    if (!btn || btn.disabled) return;

    const group = btn.closest('[data-option-index]');
    if (!group) return;
    const oi = parseInt(group.dataset.optionIndex, 10);

    // Mark explicit user selection (used by add-to-cart validation)
    group.setAttribute('data-user-selected', '');

    // Update pressed state within this option group
    group.querySelectorAll('[data-value]').forEach((b) => {
      const isThis = b === btn;
      b.setAttribute('aria-pressed', isThis ? 'true' : 'false');
      b.classList.toggle('variant-btn--selected', isThis && b.classList.contains('variant-btn'));
      b.classList.toggle('swatch-btn--selected', isThis && b.classList.contains('swatch-btn'));
    });

    this._selectedOptions[oi] = btn.dataset.value;

    // Update the "Size: X" selected-value label
    const selectedLabel = group.querySelector('[data-selected-value]');
    if (selectedLabel) selectedLabel.textContent = ': ' + btn.dataset.value;

    const variant = this._findVariant(this._selectedOptions);
    this._updateForVariant(variant);
    this._updateAvailability();
  }

  _findVariant(selected) {
    if (!this._product) return null;
    // Require a full, exact match across every option dimension. A partial
    // match (any null slot) is treated as "no variant" so price/compare-at/
    // variant-id never reflect a variant the shopper did not fully choose.
    if (selected.some((v) => v == null)) return null;
    return (
      this._product.variants.find((v) =>
        v.options.every((opt, idx) => opt === selected[idx])
      ) || null
    );
  }

  _updateForVariant(variant) {
    const priceEl = this._section.querySelector('[data-price]');
    const compareEl = this._section.querySelector('[data-compare-price]');
    const variantIdInput = this._section.querySelector('[data-variant-id]');
    const addBtn = this._section.querySelector('[data-add-to-cart]');
    const buyNowBtn = this._section.querySelector('[data-buy-now]');

    if (!variant) {
      // No variant resolves for this option combination. The hidden input must
      // be cleared: leaving the previously-resolved id in place lets Buy Now
      // (which never reads `variant`) POST a variant the shopper never chose.
      // Both purchase paths are disabled — Buy Now bypasses add-to-cart.
      if (variantIdInput) variantIdInput.value = '';
      if (addBtn) addBtn.disabled = true;
      if (buyNowBtn) buyNowBtn.disabled = true;
      this._announceVariant(null);
      this._syncVariantUrl(null);
      return;
    }

    if (priceEl) priceEl.textContent = this._formatMoney(variant.price);
    if (compareEl) {
      const amountEl = compareEl.querySelector('[data-compare-amount]');
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (amountEl) amountEl.textContent = this._formatMoney(variant.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
    if (variantIdInput) variantIdInput.value = variant.id;

    if (addBtn) addBtn.disabled = !variant.available;
    if (buyNowBtn) buyNowBtn.disabled = !variant.available;

    this._announceVariant(variant);
    this._syncVariantUrl(variant);
    this._updateGalleryImage(variant);
  }

  /**
   * One event per resolved selection, so anything that has to follow the variant
   * (today: the quantity stepper's stock ceiling) can listen instead of polling the
   * hidden input, whose programmatic writes fire no `change`.
   */
  _announceVariant(variant) {
    this.dispatchEvent(
      new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant: variant || null, variantId: variant ? String(variant.id) : '' }
      })
    );
  }

  /**
   * Reflect the selected variant in the address bar — but only on the product
   * page. This same component is reused inside the quick-view modal, which is
   * opened from collection/home pages; writing ?variant= onto those URLs leaks
   * a product-scoped param into a collection URL that outlives the modal.
   */
  _syncVariantUrl(variant) {
    if (!window.history || !window.history.replaceState) return;
    if (!this._section.classList || !this._section.classList.contains('main-product')) return;

    const url = new URL(window.location.href);
    if (variant) {
      url.searchParams.set('variant', variant.id);
    } else {
      url.searchParams.delete('variant');
    }
    window.history.replaceState({}, '', url.toString());
  }

  _updateGalleryImage(variant) {
    if (!variant.featured_image || !variant.featured_image.src) return;
    try {
      const base = variant.featured_image.src.split('?')[0].split('/').pop();
      const images = Array.from(this._section.querySelectorAll('[data-gallery-image]'));
      const match = images.find((img) => {
        const src = img.currentSrc || img.src || '';
        const hi = img.dataset.highres || '';
        return src.indexOf(base) > -1 || hi.indexOf(base) > -1;
      });
      if (!match) return;

      // Prefer the gallery's own activation (switches the desktop Amazon stage,
      // its thumbnail, and the mobile carousel together); fall back to a plain
      // scroll when the gallery component isn't present (e.g. quick view).
      const gallery = this._section.querySelector('product-gallery');
      const item = match.closest('[data-gallery-item]');
      const idx = item ? parseInt(item.dataset.index, 10) : NaN;
      if (gallery && typeof gallery.setActive === 'function' && !isNaN(idx)) {
        gallery.setActive(idx);
      } else if (typeof match.scrollIntoView === 'function') {
        match.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    } catch (e) {
      /* gallery sync is a non-critical enhancement */
    }
  }

  /**
   * Cross-variant availability (Pitfall 1): for each option value, mark it sold-out
   * when no available variant exists that pairs it with the currently-selected other options.
   */
  _updateAvailability() {
    let selectionCleared = false;

    this._optionGroups.forEach((group) => {
      const oi = parseInt(group.dataset.optionIndex, 10);
      group.querySelectorAll('[data-value]').forEach((btn) => {
        const val = btn.dataset.value;
        const available = this._product.variants.some(
          (v) =>
            v.options[oi] === val &&
            v.available &&
            this._selectedOptions.every((sel, idx) => idx === oi || sel == null || v.options[idx] === sel)
        );
        const isSwatch = btn.classList.contains('swatch-btn');
        btn.classList.toggle(isSwatch ? 'swatch-btn--sold-out' : 'variant-btn--sold-out', !available);
        btn.disabled = !available;
        if (!available) {
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.removeAttribute('aria-disabled');
        }

        // If the value the shopper currently has selected just became
        // unavailable, clear its pressed/selected state and drop it from
        // _selectedOptions so the UI does not show it as selected + sold-out
        // and _findVariant stops resolving against a dead value (WR-02).
        if (!available && this._selectedOptions[oi] === val && btn.getAttribute('aria-pressed') === 'true') {
          btn.setAttribute('aria-pressed', 'false');
          btn.classList.remove('variant-btn--selected', 'swatch-btn--selected');
          this._selectedOptions[oi] = null;
          selectionCleared = true;

          const selectedLabel = group.querySelector('[data-selected-value]');
          if (selectedLabel) selectedLabel.textContent = '';
        }
      });
    });

    // A cleared selection means price/variant-id/add-to-cart state must be
    // re-derived from the (now partial) selection.
    if (selectionCleared) {
      this._updateForVariant(this._findVariant(this._selectedOptions));
    }
  }

  /**
   * Prices in the embedded JSON are integer cents. Rendering them must match
   * the server-rendered `money` filter exactly, otherwise the price visibly
   * reformats (₹4,500.00 → ₹4,500) the moment a variant is clicked.
   */
  _formatMoney(cents) {
    return window.Theme && window.Theme.formatMoney
      ? window.Theme.formatMoney(cents)
      : String(cents);
  }
}

/* ============================================================
   ADD TO CART FORM
   ============================================================ */

class AddToCartForm extends HTMLElement {
  constructor() {
    super();
    this._boundSubmit = this._handleSubmit.bind(this);
    this._boundBuyNow = this._handleBuyNow.bind(this);
    this._loading = false;
  }

  connectedCallback() {
    this._init();
    this.addEventListener('shopify:section:load', () => this._reinit());
  }

  disconnectedCallback() {
    if (this._form) this._form.removeEventListener('submit', this._boundSubmit);
    if (this._buyNowButton) this._buyNowButton.removeEventListener('click', this._boundBuyNow);
  }

  _init() {
    this._section = this.closest('.main-product, .quick-view') || document;
    this._form = this.querySelector('form');
    this._button = this.querySelector('[data-add-to-cart]');
    this._buyNowButton = this.querySelector('[data-buy-now]');
    this._errorEl = this.querySelector('[data-form-error]');
    this._strings = {
      add: this.dataset.strAdd || 'Add to cart',
      adding: this.dataset.strAdding || 'Adding...',
      buyNow: this.dataset.strBuyNow || 'Buy it now',
      redirecting: this.dataset.strRedirecting || 'Redirecting…',
      selectSize: this.dataset.strSelectSize || 'Please select a size',
      selectColor: this.dataset.strSelectColor || 'Please select an option',
      selectOption: this.dataset.strSelectOption || 'Please choose an option',
      outOfStock: this.dataset.strOutOfStock || 'This item is currently out of stock',
      unavailable: this.dataset.strUnavailable || 'That combination is unavailable',
      maxQuantity: this.dataset.strMaxQuantity || 'Only [N] left in stock',
      generic: this.dataset.strGeneric || 'Something went wrong. Please try again.'
    };
    if (this._form) this._form.addEventListener('submit', this._boundSubmit);
    if (this._buyNowButton) this._buyNowButton.addEventListener('click', this._boundBuyNow);
  }

  _reinit() {
    if (this._form) this._form.removeEventListener('submit', this._boundSubmit);
    if (this._buyNowButton) this._buyNowButton.removeEventListener('click', this._boundBuyNow);
    this._init();
  }

  /**
   * Shared validation for both Add to Cart and Buy Now. Enforces explicit
   * option selection (Pitfall 3) and resolves the variant id + quantity.
   * Returns { variantId, quantity } or null (and surfaces an error) on failure.
   */
  _getValidatedOrder() {
    this._clearError();

    // Validation: every option group must be explicitly chosen by the user (Pitfall 3)
    const groups = Array.from(this._section.querySelectorAll('.variant-option'));
    for (const group of groups) {
      if (group.hasAttribute('data-user-selected')) continue;
      const name = (group.dataset.optionName || '').toLowerCase();
      const isSwatch = group.classList.contains('variant-option--swatch');
      // Only enforce when there is a real choice (more than one value)
      const values = group.querySelectorAll('[data-value]');
      if (values.length <= 1) continue;
      if (name.indexOf('size') > -1) {
        this._showError(this._strings.selectSize);
        return null;
      }
      if (isSwatch) {
        this._showError(this._strings.selectColor);
        return null;
      }
      // Generic fallback: any other multi-value option group (e.g. Length,
      // Sleeve) that was never explicitly chosen must also block submission
      // so it cannot silently default to the SSR value (Pitfall 3, WR-07).
      this._showError(this._strings.selectOption);
      return null;
    }

    // An empty variant id means every option group was chosen but the resulting
    // combination has no matching variant (VariantSelector clears it). That is a
    // shopper-facing state, not an internal error.
    const variantInput = this._section.querySelector('[data-variant-id]');
    const variantId = variantInput ? parseInt(variantInput.value, 10) : NaN;
    if (!variantId || isNaN(variantId)) {
      this._showError(this._strings.unavailable);
      return null;
    }

    // Quantity comes from the stepper when the merchant shows one; a form without one
    // buys a single unit.
    const stepper = this._section.querySelector('quantity-stepper');
    const qtyInput = this._section.querySelector('[data-quantity]');
    let quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    if (!quantity || isNaN(quantity) || quantity < 1) quantity = 1;

    // Stock ceiling. The stepper already blocks the "+" at the limit, but a shopper can
    // type past it, and the variant can change under a quantity that was legal for the
    // previous one — so the purchase paths re-check rather than trust the input's value.
    // A ceiling of 0 means the variant sold out: neither path may proceed.
    const max = stepper && typeof stepper.max === 'number' ? stepper.max : -1;
    if (max === 0) {
      this._showError(this._strings.outOfStock);
      return null;
    }
    if (max > 0 && quantity > max) {
      if (stepper && typeof stepper.setQuantity === 'function') stepper.setQuantity(max);
      else if (qtyInput) qtyInput.value = max;
      this._showError(this._strings.maxQuantity.replace('[N]', max));
      return null;
    }

    return { variantId: variantId, quantity: quantity };
  }

  _handleSubmit(e) {
    e.preventDefault();
    if (this._loading) return;

    const order = this._getValidatedOrder();
    if (!order) return;

    this._setLoading(true);

    const url =
      (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: order.variantId, quantity: order.quantity }],
        sections: 'cart-drawer',
        sections_url: window.location.pathname
      })
    })
      .then((r) => r.json().then((body) => ({ status: r.status, body: body })))
      .then(({ status, body }) => {
        if (status === 422) {
          this._showError(body.description || this._strings.outOfStock);
          return;
        }
        if (status >= 400) {
          this._showError(body.description || this._strings.generic);
          return;
        }
        if (window.CartDrawerHelpers && window.CartDrawerHelpers.openCartDrawerWithContent) {
          window.CartDrawerHelpers.openCartDrawerWithContent({
            triggerElement: this._button,
            sections: body.sections
          });
        }
      })
      .catch(() => {
        this._showError(this._strings.generic);
      })
      .finally(() => {
        this._setLoading(false);
      });
  }

  /**
   * Buy Now — add the selected variant to the cart, then send the shopper
   * straight to checkout. Reuses the same validation as Add to Cart, and the
   * same in-flight guard so a double-tap can't double-add (T-03-08).
   */
  _handleBuyNow(e) {
    if (e) e.preventDefault();
    if (this._loading) return;

    const order = this._getValidatedOrder();
    if (!order) return;

    this._setBuyNowLoading(true);

    const url =
      (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: order.variantId, quantity: order.quantity }] })
    })
      .then((r) => r.json().then((body) => ({ status: r.status, body: body })))
      .then(({ status, body }) => {
        if (status === 422) {
          this._showError(body.description || this._strings.outOfStock);
          this._setBuyNowLoading(false);
          return;
        }
        if (status >= 400) {
          this._showError(body.description || this._strings.generic);
          this._setBuyNowLoading(false);
          return;
        }
        // Success — leave the button in its loading state and navigate. The
        // page unload tears the component down, so there's no flicker back.
        window.location.href = '/checkout';
      })
      .catch(() => {
        this._showError(this._strings.generic);
        this._setBuyNowLoading(false);
      });
  }

  _setBuyNowLoading(on) {
    this._loading = on;
    if (this._button) this._button.disabled = on;
    if (!this._buyNowButton) return;
    this._buyNowButton.disabled = on;
    this._buyNowButton.textContent = on ? this._strings.redirecting : this._strings.buyNow;
  }

  _setLoading(on) {
    this._loading = on;
    if (!this._button) return;
    this._button.disabled = on;
    this._button.textContent = on ? this._strings.adding : this._strings.add;
  }

  _showError(message) {
    if (!this._errorEl) return;
    this._errorEl.textContent = message;
    this._errorEl.hidden = false;
  }

  _clearError() {
    if (!this._errorEl) return;
    this._errorEl.textContent = '';
    this._errorEl.hidden = true;
  }
}

/* ============================================================
   QUANTITY STEPPER — −/+ controls around a number input

   Feeds the [data-quantity] input that AddToCartForm reads when building the
   /cart/add.js payload, and holds the stock ceiling for the SELECTED variant:

     - `max` is re-read from the sibling [data-inventory-map] on every variant:change,
       because stock is per-variant: 2 left in M says nothing about L.
     - -1 means no ceiling — Shopify isn't tracking the variant, or it oversells on
       purpose. Capping those would refuse orders the merchant wants.
     - At the ceiling, "+" disables and the note names the real number. Typing past it
       snaps back down, so the field can never carry a quantity the shop can't ship.

   The clamp is a courtesy, not a guarantee: /cart/add.js re-checks inventory and 422s
   if the last unit went while the shopper was deciding.
   ============================================================ */

class QuantityStepper extends HTMLElement {
  connectedCallback() {
    this._input = this.querySelector('[data-quantity]');
    this._dec = this.querySelector('[data-qty-decrease]');
    this._inc = this.querySelector('[data-qty-increase]');
    this._min = parseInt(this.dataset.min, 10) || 1;
    this._max = this._parseMax(this.dataset.max);
    this._maxTemplate = this.dataset.strMax || 'Only [N] left in stock';

    // Scope to the PDP section or the quick-view modal — never `document`, so a quick
    // view opened over a PDP reads its own inventory map and note, not the page's.
    this._scope = this.closest('.main-product, .quick-view') || document;
    this._note = this._scope.querySelector('[data-qty-note]');
    this._map = this._readInventoryMap();

    this._onDec = () => this._step(-1);
    this._onInc = () => this._step(1);
    this._onChange = () => this._clamp();
    this._onVariant = (e) => this._applyVariant(e.detail && e.detail.variantId);

    if (this._dec) this._dec.addEventListener('click', this._onDec);
    if (this._inc) this._inc.addEventListener('click', this._onInc);
    if (this._input) this._input.addEventListener('change', this._onChange);
    this._scope.addEventListener('variant:change', this._onVariant);

    this._sync(false);
  }

  disconnectedCallback() {
    if (this._dec) this._dec.removeEventListener('click', this._onDec);
    if (this._inc) this._inc.removeEventListener('click', this._onInc);
    if (this._input) this._input.removeEventListener('change', this._onChange);
    if (this._scope) this._scope.removeEventListener('variant:change', this._onVariant);
  }

  /** Units the shopper may still buy of the selected variant; -1 when uncapped. */
  get max() {
    return this._max;
  }

  setQuantity(n) {
    if (!this._input) return;
    this._input.value = this._bound(n);
    this._sync(true);
  }

  _readInventoryMap() {
    const el = this._scope.querySelector('[data-inventory-map]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  _parseMax(raw) {
    const n = parseInt(raw, 10);
    return isNaN(n) || n < 0 ? -1 : n;
  }

  /**
   * A variant with no map entry falls back to uncapped rather than to zero: an unknown
   * stock level is not evidence of an empty one, and guessing zero would refuse a sale.
   * An unresolved combination (no variant) leaves the ceiling alone — Add to cart is
   * already disabled, so there is nothing to clamp against.
   */
  _applyVariant(variantId) {
    if (!variantId || !this._map) return;
    const entry = this._map[variantId];
    this._max = typeof entry === 'number' && entry >= 0 ? entry : -1;

    // Only speak up if the new ceiling actually took units away from them. Switching to a
    // variant with 2 left while they're buying 1 is not news — the low-stock line already
    // carries that. Being silently dropped from 3 to 2 is.
    const wanted = this._current();
    this._sync(this._bound(wanted) < wanted);
  }

  _current() {
    const v = parseInt(this._input && this._input.value, 10);
    return isNaN(v) ? this._min : v;
  }

  _bound(n) {
    let v = Math.max(this._min, n);
    if (this._max > -1) v = Math.min(v, Math.max(this._max, 0));
    return v;
  }

  _step(delta) {
    this.setQuantity(this._current() + delta);
  }

  _clamp() {
    this.setQuantity(this._current());
  }

  /**
   * Reflect the current value + ceiling into the input, the buttons and the note.
   * `notify` is false for the first paint: the note is feedback for an action the shopper
   * took, not a banner to greet them with.
   */
  _sync(notify) {
    if (!this._input) return;

    const capped = this._max > -1;
    const value = this._bound(this._current());
    if (String(value) !== this._input.value) this._input.value = value;

    if (capped) this._input.max = this._max;
    else this._input.removeAttribute('max');

    if (this._dec) this._dec.disabled = value <= this._min;
    if (this._inc) this._inc.disabled = capped && value >= this._max;

    // The note earns its row only once the shopper is actually held at the ceiling.
    if (this._note) {
      const atCeiling = notify && capped && this._max > 0 && value >= this._max;
      if (atCeiling) {
        this._note.textContent = this._maxTemplate.replace('[N]', this._max);
        this._note.hidden = false;
      } else {
        this._note.textContent = '';
        this._note.hidden = true;
      }
    }
  }
}

if (!customElements.get('variant-selector')) {
  customElements.define('variant-selector', VariantSelector);
}
if (!customElements.get('quantity-stepper')) {
  customElements.define('quantity-stepper', QuantityStepper);
}
if (!customElements.get('add-to-cart-form')) {
  customElements.define('add-to-cart-form', AddToCartForm);
}
